import usePageTitle from "@/hooks/use-page-title";
import { Truck, Package, Clock, MapPin, IndianRupee, Info } from "lucide-react";

export default function ShippingDelivery() {
  usePageTitle("Shipping & Delivery");
  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 py-14">
      <div className="overline text-[#FF6B00] mb-2">Policy</div>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Shipping & Delivery</h1>
      <p className="mt-3 text-zinc-600">Everything you need to know about how your printed order gets to you.</p>

      <div className="mt-10 grid md:grid-cols-2 gap-4">
        <Card icon={IndianRupee} title="Shipping Charges">
          <ul className="list-disc pl-4 space-y-1">
            <li>Orders ≥ ₹1,000 — <b>FREE Shipping</b> pan-India</li>
            <li>Orders below ₹1,000 — flat ₹60 shipping</li>
            <li>Large-format / heavy items (flex, signage, standees) — actual courier charges quoted separately</li>
          </ul>
        </Card>
        <Card icon={Clock} title="Production Time">
          <ul className="list-disc pl-4 space-y-1">
            <li>Standard: <b>3–5 business days</b> after design approval</li>
            <li>Same-day printing available for select products confirmed before 5 PM</li>
            <li>Bulk / signage projects: quoted on order</li>
          </ul>
        </Card>
        <Card icon={Truck} title="Dispatch & Delivery">
          <ul className="list-disc pl-4 space-y-1">
            <li>Navi Mumbai / Mumbai: 24–48 hours after dispatch (self-pickup available at our office)</li>
            <li>Rest of India: 3–7 business days via reputed courier partners</li>
            <li>Tracking number shared over SMS / WhatsApp / Email as soon as dispatched</li>
          </ul>
        </Card>
        <Card icon={MapPin} title="Service Area">
          <ul className="list-disc pl-4 space-y-1">
            <li>Delivery available across India (via courier)</li>
            <li>Cash on Delivery available within Navi Mumbai / Mumbai only</li>
            <li>Serviceable PIN codes verified at checkout</li>
          </ul>
        </Card>
        <Card icon={Package} title="Packaging">
          <ul className="list-disc pl-4 space-y-1">
            <li>All prints double-packed in bubble wrap or rigid cardboard tubes (large-format)</li>
            <li>Fragile items (canvas, LED signs) — reinforced protective packing</li>
            <li>Discreet packing available on request</li>
          </ul>
        </Card>
        <Card icon={Info} title="Delays & Exceptions">
          <ul className="list-disc pl-4 space-y-1">
            <li>Design revisions on the customer side extend production timelines</li>
            <li>Delivery timelines exclude Sundays and national holidays</li>
            <li>Courier delays outside our control — we ship on time, we can't control transit hiccups</li>
          </ul>
        </Card>
      </div>

      <div className="mt-10 p-6 rounded-2xl border-2 border-orange-100 bg-orange-50/70">
        <div className="font-bold text-lg mb-1">Need something urgent?</div>
        <p className="text-sm text-zinc-700">Call our Customer Care on <a href="tel:02247427059" className="text-[#FF6B00] font-bold hover:underline">022 4742 7059</a> or WhatsApp us on <a href="https://wa.me/918070970980" className="text-[#25D366] font-bold hover:underline">+91 80709 70980</a> — we do our best to accommodate rush orders and hand-deliver within Navi Mumbai.</p>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="p-6 rounded-2xl border-2 border-zinc-100 bg-white hover:border-[#FF6B00] transition-colors">
      <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mb-3">
        <Icon size={18} className="text-[#FF6B00]" />
      </div>
      <div className="font-bold text-lg mb-2">{title}</div>
      <div className="text-sm text-zinc-600 leading-relaxed">{children}</div>
    </div>
  );
}
